import { useMemo } from 'react';

import { GatewayRuleMappingMethod } from 'pages/GatewayRules/components/GatewayRuleMapping/GatewayRuleMappingMethod';
import { GatewayRulesFormRemoveButton } from 'pages/GatewayRules/components/GatewayRulesForm/GatewayRulesFormRemoveButton';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { EditIconButton } from 'shared/mui/IconButtons';
import { Tooltip } from 'shared/mui/Tooltip';
import { AutocompleteInput, BooleanInput, NumberInput, TextInput } from 'shared/react-admin/Inputs';
import { MethodLogo } from 'types/embedded/methodLogo';
import { jsonEqual } from 'utils/jsonEqual';

import { useGatewayRulesDictionaryContext } from '../context/GatewayRulesDictionaryContext';
import { GatewayRulesFormSkeletonMappingHead } from '../GatewayRulesFormSkeleton/GatewayRulesFormSkeletonMappingHead';
import { getInitialMapping } from '../utils';
import { useMethodChoices } from './hooks/useMethodChoices';

type Props = {
  mappingIndex: number;
  enableAdditionalFields: boolean;
  onChangeMethod: (index: number) => void;
  onChangeDirection: (index: number) => void;
  onRemoveMappingClick: (index: number) => void;
  onEditDescriptionClick: () => void;
  methodLogo: MethodLogo | undefined;
  isLoadingMethod: boolean;
};

export const GatewayRulesFormMappingHead = ({
  mappingIndex,
  enableAdditionalFields,
  onChangeMethod,
  onChangeDirection,
  onEditDescriptionClick,
  onRemoveMappingClick,
  methodLogo,
  isLoadingMethod,
}: Props) => {
  const { directions } = useGatewayRulesDictionaryContext();
  const { methodChoices } = useMethodChoices(mappingIndex);

  const translate = useTypedTranslate();
  const { getValues, formState, watch } = useFormContext();

  const active = watch(`mappings.${mappingIndex}.active`);
  const merchantWatched = watch('merchant');

  const isDisabledRemoveMapping = useMemo<boolean>(() => {
    const currentValues = getValues();
    return (
      currentValues.mappings.length === 1 &&
      jsonEqual(currentValues.mappings[0], getInitialMapping())
    );
  }, [formState]);

  if (isLoadingMethod) {
    return (
      <CardContent sx={{ backgroundColor: '#FAFAFA' }}>
        <GatewayRulesFormSkeletonMappingHead enableAdditionalFields={enableAdditionalFields} />
      </CardContent>
    );
  }

  return (
    <CardContent sx={{ backgroundColor: '#FAFAFA' }}>
      <Box display="flex" gap="16px" justifyContent="space-between">
        <AutocompleteInput
          choices={methodChoices}
          fullWidth
          isRequired
          label={translate('app.table.method')}
          name={`mappings.${mappingIndex}.method`}
          onChange={() => onChangeMethod(mappingIndex)}
          optionText="name"
          readOnly={!merchantWatched}
          size="small"
          source={`mappings.${mappingIndex}.method`}
          sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
          variant="outlined"
        />
        <GatewayRuleMappingMethod
          isForm
          isLoadingMethod={isLoadingMethod}
          methodLogo={methodLogo}
        />
        <AutocompleteInput
          choices={directions}
          fullWidth
          isRequired
          label={translate('app.table.direction')}
          name={`mappings.${mappingIndex}.direction`}
          onChange={() => onChangeDirection(mappingIndex)}
          optionText="description"
          size="small"
          source={`mappings.${mappingIndex}.direction`}
          sx={{ '.MuiInputBase-root': { backgroundColor: 'white' } }}
          variant="outlined"
        />
        <Tooltip
          arrow
          placement="top"
          title={
            active
              ? translate('app.gatewayRules.mappingIsActive')
              : translate('app.gatewayRules.mappingIsInactive')
          }
        >
          <Box>
            <BooleanInput
              fullWidth
              label={translate('app.table.active')}
              name={`mappings.${mappingIndex}.active`}
              source="active"
            />
          </Box>
        </Tooltip>
        <GatewayRulesFormRemoveButton
          disabled={isDisabledRemoveMapping}
          onClick={() => onRemoveMappingClick(mappingIndex)}
          tooltipText={translate('app.gatewayRules.deleteMapping')}
        />
      </Box>
      {enableAdditionalFields && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', alignSelf: 'stretch' }}>
          <NumberInput
            InputProps={{ sx: { backgroundColor: 'white' } }}
            defaultValue={0}
            label={translate('app.table.sortOrder')}
            name={`mappings.${mappingIndex}.sortOrder`}
            parse={(v) => Number(v)}
            size="small"
            source={`mappings.${mappingIndex}.sortOrder`}
            sx={{ minWidth: '153px', maxWidth: '153px' }}
            variant="outlined"
          />
          <TextInput
            InputProps={{
              sx: { backgroundColor: 'white' },
              endAdornment: <EditIconButton onClick={onEditDescriptionClick} />,
            }}
            fullWidth
            label={translate('app.table.description')}
            name={`mappings.${mappingIndex}.description`}
            size="small"
            source={`mappings.${mappingIndex}.description`}
            variant="outlined"
          />
          <TextInput
            InputProps={{ sx: { backgroundColor: 'white' } }}
            fullWidth
            label={translate('app.gatewayRules.methodMerchantsName')}
            name={`mappings.${mappingIndex}.merchantLabel`}
            size="small"
            source={`mappings.${mappingIndex}.merchantLabel`}
            variant="outlined"
          />
        </Box>
      )}
    </CardContent>
  );
};
